/**
 * lineMedia.tsx — LINE の受信画像・動画を期限付き署名URLで表示する（2026-10-09）
 *
 * <img src> / <video src> は Authorization ヘッダを付けられない。以前は URL に共有キー（api_key）を
 * 載せていたが、そのキーが公開バンドルに漏れていたため廃止した。代わりにログイン済みスタッフとして
 * sign_message_content を呼び、サーバーが発行する1時間有効の署名付きURLを使う（署名鍵はサーバーのみ）。
 */
import React, { useEffect, useState } from 'react';
import { logEvent } from './eventLog';

export const LINE_API_URL =
  process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';

type CacheEntry = { url: string; expiresAt: number };
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<string | null>>();
/** 期限の5分前には取り直す */
const REFRESH_MARGIN_SEC = 300;

/** 署名付きURLを取得する（キャッシュ・同時リクエストの集約あり）。失敗時は null */
export function getSignedLineMediaUrl(messageId: string): Promise<string | null> {
  const now = Math.floor(Date.now() / 1000);
  const hit = cache.get(messageId);
  if (hit && hit.expiresAt - REFRESH_MARGIN_SEC > now) return Promise.resolve(hit.url);
  const pending = inflight.get(messageId);
  if (pending) return pending;

  // api_key= は internalApiAuth の目印（中身は空）。fetch 時にスタッフの Bearer に置き換わる
  const p = fetch(`${LINE_API_URL}?action=sign_message_content&message_id=${encodeURIComponent(messageId)}&api_key=`)
    .then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success || !data.url) {
        logEvent({ action: 'line.media.sign', target: messageId, result: 'fail', err: `HTTP ${res.status} ${data?.error ?? ''}` });
        return null;
      }
      cache.set(messageId, { url: data.url, expiresAt: Number(data.expires_at) || now + 3600 });
      return data.url as string;
    })
    .catch((err) => {
      logEvent({ action: 'line.media.sign', target: messageId, result: 'fail', err: String(err) });
      return null;
    })
    .finally(() => inflight.delete(messageId));
  inflight.set(messageId, p);
  return p;
}

/** 署名付きURLを返すフック。取得中・失敗は null */
export function useSignedLineMediaUrl(messageId: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!messageId) return null;
    const hit = cache.get(messageId);
    return hit && hit.expiresAt - REFRESH_MARGIN_SEC > Date.now() / 1000 ? hit.url : null;
  });
  useEffect(() => {
    if (!messageId) {
      setUrl(null);
      return;
    }
    let alive = true;
    getSignedLineMediaUrl(messageId).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [messageId]);
  return url;
}

/**
 * 一覧の map の中で使える描画部品。
 *   <SignedLineMedia messageId={id}>{(url) => url ? <img src={url} /> : <Placeholder />}</SignedLineMedia>
 */
export function SignedLineMedia({
  messageId,
  children,
}: {
  messageId: string;
  children: (url: string | null) => React.ReactNode;
}): React.ReactElement {
  const url = useSignedLineMediaUrl(messageId);
  return <>{children(url)}</>;
}
